import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  PLATFORM_ID,
  ViewChild,
  inject,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EnfafButtonComponent } from '../enfaf-button/enfaf-button.component';
import { EnfafIconComponent } from '../enfaf-icon/enfaf-icon.component';
import { ProgressComponent } from '../progress/progress.component';
import { PdfPreviewService } from '../../core/services/pdf-preview/pdf-preview.service';
import { ToastService } from '../../core/services/toast/toast.service';
import { FileType } from '../../models/enums/file_type.enum';
import { CustomFile, CustomUploadedFile } from '../../models/interfaces/custom_file.interface';
import { UtilsService } from '../../shared/utils/utils.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-enfaf-input-single-file-dragger-dark',
  standalone: true,
  imports: [CommonModule, EnfafIconComponent, EnfafButtonComponent, ProgressComponent],
  templateUrl: './enfaf-input-single-file-dragger-dark.component.html',
  styleUrl: './enfaf-input-single-file-dragger-dark.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EnfafInputSingleFileDraggerDarkComponent implements AfterViewInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input() accept = '*';
  @Input() maxFileSize = 250000000;
  @Input() cleanFiles: Subject<unknown> = new Subject();
  @Input() title = 'Arrastra o suelta aquí';
  @Input() subtitle = 'o';
  @Input() buttonText = 'Sube tu archivo';
  @Input() emptyStateText = 'Ningún archivo seleccionado';
  @Input() height = '200px';
  @Input() suspendInlinePreview = false;
  @Input() disabled = false;
  @Input() uploadDisabled = false;
  @Input() isUploading = false;
  @Input() showFileName = false;
  @Input() state: 'default' | 'error' | 'success' = 'default';
  @Input() activeMode = false;
  @Input() canEdit = false;

  @Input() set localFile(value: CustomFile | null | undefined) {
    this._localFile = value ? { ...value } : null;
    this.currentPdfPreviewUrl = null;
    this.isGeneratingPdfPreview = false;

    if (this._localFile?.file && this._localFile.file instanceof File) {
      const fileType = this._localFile.fileType || this.utils.getFileTypeByFileName(this._localFile.name);

      if (!this._localFile.fileType) {
        this._localFile.fileType = fileType;
      }

      if ((fileType === FileType.Image || fileType === FileType.Document) && !this._localFile.blobUrl) {
        this._localFile.blobUrl = this.createAndCacheObjectURL(this._localFile.file);
      }

      if (fileType === FileType.Document && !this._localFile.previewSrc) {
        this.isGeneratingPdfPreview = isPlatformBrowser(this.platformId);
        void this.generateLocalPdfPreview(this._localFile);
      }
    }

    this.changeDetectorRef.markForCheck();
  }

  get localFile(): CustomFile | null {
    return this._localFile;
  }

  @Input() set currentFile(value: CustomUploadedFile | null | undefined) {
    const currentPath = value?.path || null;
    if (this._currentFile?.path === currentPath) {
      return;
    }

    this._currentFile = value || null;

    if (this._currentFile && this.getFileType(this._currentFile) === FileType.Document) {
      void this.generateCurrentPdfPreview(this._currentFile);
    } else {
      this.currentPdfPreviewUrl = null;
    }

    this.changeDetectorRef.markForCheck();
  }

  get currentFile(): CustomUploadedFile | null {
    return this._currentFile;
  }

  @Output() filesDropped = new EventEmitter<CustomFile[]>();
  @Output() previewClicked = new EventEmitter<{ url: string; originalUrl?: string; file?: File; isPdf: boolean; fileName: string }>();
  @Output() removeClicked = new EventEmitter<void>();

  isDraggingOver = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly pdfPreview = inject(PdfPreviewService);
  private readonly toast = inject(ToastService);
  private readonly utils = inject(UtilsService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private readonly blobUrlCache = new Map<string, string>();
  private _localFile: CustomFile | null = null;
  private _currentFile: CustomUploadedFile | null = null;
  private currentPdfPreviewUrl: string | null = null;
  private isGeneratingPdfPreview = false;

  constructor() {
    this.cleanFiles.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.clearLocalFile();
    });
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
  }

  ngOnDestroy(): void {
    this.revokeBlobUrls();
  }

  get hasSelectedFile(): boolean {
    return !!(this.localFile || this.currentFile);
  }

  get isPreviewLoading(): boolean {
    return this.isUploading || this.isGeneratingPdfPreview;
  }

  get isImagePreview(): boolean {
    if (this.currentFile?.previewIsImage) {
      return true;
    }

    if (this.localFile?.file && this.getFileType(this.localFile) === FileType.Document && this.localFile.previewSrc) {
      return true;
    }

    if (this.localFile) {
      return this.getFileType(this.localFile) === FileType.Image;
    }

    if (this.currentFile) {
      return this.getFileType(this.currentFile) === FileType.Image;
    }

    return false;
  }

  get isPdfPreview(): boolean {
    if (this.currentFile?.previewIsImage || (this.localFile?.file && this.localFile.previewSrc)) {
      return false;
    }

    if (this.localFile) {
      return this.getFileType(this.localFile) === FileType.Document;
    }

    if (this.currentFile) {
      return this.getFileType(this.currentFile) === FileType.Document;
    }

    return false;
  }

  get hasVisualPreview(): boolean {
    return this.hasSelectedFile && (this.isImagePreview || this.isPdfPreview);
  }

  get showActionOverlay(): boolean {
    return this.activeMode && this.canEdit && this.hasSelectedFile && !this.disabled;
  }

  get previewImageUrl(): string | null {
    if (!this.isImagePreview) {
      return null;
    }

    if (this.localFile?.file && this.getFileType(this.localFile) === FileType.Document && this.localFile.previewSrc) {
      return typeof this.localFile.previewSrc === 'string' ? this.localFile.previewSrc : null;
    }

    if (this.localFile?.blobUrl) {
      return this.localFile.blobUrl;
    }

    return this.currentFile?.path || null;
  }

  get previewPdfUrl(): string | null {
    if (!this.isPdfPreview) {
      return null;
    }

    if (typeof this.localFile?.previewSrc === 'string' && this.localFile.previewSrc) {
      return this.localFile.previewSrc;
    }

    return this.currentPdfPreviewUrl;
  }

  get selectedFileName(): string {
    if (this.localFile) {
      return this.localFile.name;
    }

    if (this.currentFile) {
      return this.currentFile.filename;
    }

    return this.emptyStateText;
  }

  get selectedFileExtension(): string {
    const filename = this.selectedFileName;
    const extension = filename.split('.').pop()?.toUpperCase();
    return extension || 'ARCHIVO';
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled || this.uploadDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent): void {
    if (this.disabled || this.uploadDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;

    const droppedFile = event.dataTransfer?.files?.[0];

    if (!droppedFile) {
      return;
    }

    void this.handleSelectedFile(droppedFile);
  }

  triggerFileInput(): void {
    if (this.disabled || this.uploadDisabled) {
      return;
    }

    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    if (this.disabled || this.uploadDisabled) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    void this.handleSelectedFile(file);
    input.value = '';
  }

  onPreviewClick(event: Event): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.localFile) {
      this.previewClicked.emit({
        url: this.getLocalPreviewUrl(),
        file: this.localFile.file instanceof File ? this.localFile.file : undefined,
        isPdf: this.getFileType(this.localFile) === FileType.Document,
        fileName: this.localFile.name
      });
      return;
    }

    if (this.currentFile) {
      this.previewClicked.emit({
        url: this.currentFile.path,
        originalUrl: this.currentFile.originalPath,
        isPdf: this.getFileType(this.currentFile) === FileType.Document,
        fileName: this.currentFile.filename
      });
    }
  }

  private getLocalPreviewUrl(): string {
    if (this.getFileType(this.localFile as CustomFile) === FileType.Document && this.localFile?.previewSrc) {
      return typeof this.localFile.previewSrc === 'string' ? this.localFile.previewSrc : '';
    }

    return this.localFile?.blobUrl || '';
  }

  onEditClick(event: Event): void {
    if (this.disabled || this.uploadDisabled || !this.activeMode) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.triggerFileInput();
  }

  onRemoveClick(event: Event): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.clearLocalFile();
    this.changeDetectorRef.markForCheck();
    this.removeClicked.emit();
  }

  private async handleSelectedFile(file: File): Promise<void> {
    const result = await this.processFile(file);

    if (result.errorMsg) {
      this.toast.presentErrorToast(result.errorMsg, 10000);
      return;
    }

    if (!result.file || !result.fileType) {
      return;
    }

    this.clearLocalFile();

    const nextFile: CustomFile = {
      name: file.name,
      file: result.file,
      fileType: result.fileType,
      previewSrc: result.previewSrc || '',
      blobUrl: result.fileType === FileType.Image || result.fileType === FileType.Document ? this.createAndCacheObjectURL(result.file) : undefined
    };

    this._localFile = nextFile;

    if (result.fileType === FileType.Document && isPlatformBrowser(this.platformId)) {
      this.isGeneratingPdfPreview = true;
      void this.generateLocalPdfPreview(nextFile);
    }

    this.filesDropped.emit([nextFile]);
    this.changeDetectorRef.markForCheck();
  }

  private async processFile(file: File): Promise<{
    errorMsg?: string;
    file?: File;
    previewSrc?: string | null;
    fileType?: FileType;
  }> {
    try {
      const fileName = file.name;
      const fileType = this.getBrowserFileType(file, fileName);

      if (!this.isAllowedFile(file)) {
        return { errorMsg: `- El archivo '${fileName}' debe ser de un formato válido (${this.accept})\n` };
      }

      if (this.maxFileSize && file.size > this.maxFileSize) {
        return { errorMsg: `- El archivo '${fileName}' no puede exceder los ${this.maxFileSize / this.utils.oneMegaByte} MB\n` };
      }

      if (fileType === FileType.Image && file.size > this.utils.maxImageSize) {
        return { errorMsg: `- La imagen '${fileName}' no puede exceder los ${this.utils.maxImageSize / this.utils.oneMegaByte} MB\n` };
      }

      if (fileType === FileType.Document && file.size > this.utils.maxDocumentSize) {
        return { errorMsg: `- El documento '${fileName}' no puede exceder los ${this.utils.maxDocumentSize / this.utils.oneMegaByte} MB\n` };
      }

      if (file.size === 0) {
        return { errorMsg: `- El archivo '${fileName}' no puede estar vacío\n` };
      }

      return { file, fileType, previewSrc: null };
    } catch {
      return { errorMsg: '- Error al procesar el archivo\n' };
    }
  }

  private isAllowedFile(file: File): boolean {
    if (this.accept === '*') {
      return true;
    }

    const allowedTypes = this.accept.split(',').map((type) => type.trim());
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    return allowedTypes.some((type) => {
      if (type.startsWith('.')) {
        return fileExtension === type.substring(1).toLowerCase();
      }

      if (type.includes('/*')) {
        return file.type.startsWith(type.split('/')[0]);
      }

      return file.type === type;
    });
  }

  private getBrowserFileType(file: File, fileName: string): FileType {
    if (file.type.indexOf(FileType.Image) > -1) {
      return FileType.Image;
    }

    if (file.type.indexOf(FileType.Video) > -1) {
      return FileType.Video;
    }

    if (file.type.indexOf('pdf') > -1) {
      return FileType.Document;
    }

    return this.utils.getFileTypeByFileName(fileName) as FileType;
  }

  private getFileType(file: CustomFile | CustomUploadedFile): FileType {
    const fileName = 'filename' in file ? file.filename : file.name;
    return this.utils.getFileTypeByFileName(fileName) as FileType;
  }

  private clearLocalFile(): void {
    if (this._localFile?.blobUrl && this._localFile.file) {
      URL.revokeObjectURL(this._localFile.blobUrl);
      this.blobUrlCache.delete(this.getFileKey(this._localFile.file));
    }

    this._localFile = null;
    this.isGeneratingPdfPreview = false;
  }

  private revokeBlobUrls(): void {
    this.blobUrlCache.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    this.blobUrlCache.clear();
  }

  private createAndCacheObjectURL(file: File): string {
    const key = this.getFileKey(file);
    let url = this.blobUrlCache.get(key);

    if (!url) {
      url = URL.createObjectURL(file);
      this.blobUrlCache.set(key, url);
    }

    return url;
  }

  private getFileKey(file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}`;
  }

  private async generateLocalPdfPreview(file: CustomFile): Promise<void> {
    if (!file.file || this.getFileType(file) !== FileType.Document || !isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const previewSrc = await this.pdfPreview.generatePreviewFromFile(file.file, {
        maxWidth: 550,
        maxHeight: this.parseHeight(this.height)
      });

      const currentLocalFile = this._localFile;

      if (!currentLocalFile || currentLocalFile.file !== file.file) {
        return;
      }

      currentLocalFile.previewSrc = previewSrc;
    } catch {
    } finally {
      if (this._localFile?.file === file.file) {
        this.isGeneratingPdfPreview = false;
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  private async generateCurrentPdfPreview(file: CustomUploadedFile): Promise<void> {
    if (this.getFileType(file) !== FileType.Document || !file.path || !isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const previewSrc = await this.pdfPreview.generatePreviewFromUrl(file.path, {
        maxWidth: 550,
        maxHeight: this.parseHeight(this.height)
      });

      if (this._currentFile?.path === file.path) {
        this.currentPdfPreviewUrl = previewSrc;
        this.changeDetectorRef.markForCheck();
      }
    } catch {
      if (this._currentFile?.path === file.path) {
        this.currentPdfPreviewUrl = null;
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  private parseHeight(value: string): number {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 300;
  }
}
